import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  CheckCircle,
  FileText,
  LayoutDashboard,
  Loader2,
  Plus,
  Search,
  Ship,
  UserPlus,
} from 'lucide-react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from 'recharts';
import { setupAxios } from '../../auth';
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext';
import SearchableSelect from '../../../shared/components/SearchableSelect'
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

/* ─────────────────────────  MOCK DATA  ───────────────────────── */

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Compute derived fields
const enrichVessels = (vessels: any[], timeFraction: number) => vessels.map(v => {
  const remaining = v.allocated - v.utilized;
  const variance = v.utilized - (v.allocated * timeFraction);
  const elapsedMonths = timeFraction * 12;
  const avgMonthlyBurn = elapsedMonths > 0 ? v.utilized / elapsedMonths : 0;
  const fac = v.utilized + avgMonthlyBurn * Math.max(0, 12 - elapsedMonths);
  const utilizationPct = v.allocated > 0 ? (v.utilized / v.allocated) * 100 : 0;
  const timePct = timeFraction * 100;
  const budgetPerDay = v.utilized / (v.operatingDays || 1);
  const costPerWeek = budgetPerDay * 7;

  // Health score: weighted (lower = worse)
  const variancePenalty = v.allocated > 0 ? Math.max(0, variance) / v.allocated * 100 : 0;
  const facBreachPenalty = v.allocated > 0 && fac > v.allocated ? ((fac - v.allocated) / v.allocated) * 100 : 0;
  const burnRatePenalty = utilizationPct > timePct ? (utilizationPct - timePct) : 0;
  const rawScore = 100 - variancePenalty * 1.5 - facBreachPenalty * 2 - burnRatePenalty * 0.8;
  const healthScore = Math.max(0, Math.min(100, rawScore));
  const healthLabel = healthScore >= 70 ? 'Green' : healthScore >= 40 ? 'Amber' : 'Red';

  return { ...v, remaining, variance, fac, utilizationPct, timePct, avgMonthlyBurn, budgetPerDay, costPerWeek, healthScore, healthLabel };
});

// Exception alerts — dynamically generated below inside the component

/* ─────────────────────────  HELPERS  ───────────────────────── */

const safeNumber = (val: any): number => {
  let num = Number(val);
  if (isNaN(num)) {
    const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : '';
    num = Number(clean);
    if (isNaN(num)) num = 0;
  }
  return num;
};

const fmt = (val: any) => {
  const num = safeNumber(val);
  if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

const formatCurrency = (val: any) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(val));
};

const fmtFull = (val: any) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(safeNumber(val));

const pct = (val: any) => `${safeNumber(val).toFixed(1)}%`;

/* ─────────────────────  SPARKLINE COMPONENT  ─────────────────────── */

function Sparkline({ data, color = '#3b82f6', height = 28, width = 80 }) {
  const validData = data.filter(d => d > 0);
  if (validData.length < 2) return null;
  const max = Math.max(...validData);
  const min = Math.min(...validData);
  const range = max - min || 1;
  const points = validData.map((d, i) => {
    const x = (i / (validData.length - 1)) * width;
    const y = height - ((d - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="shrink-0">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ──────────────────  MINI PROGRESS BAR  ──────────────────── */

function MiniProgress({ value, max = 100, color = 'bg-blue-500', bgColor = 'bg-slate-100' }) {
  const w = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className={`w-full h-1.5 rounded-full ${bgColor} overflow-hidden`}>
      <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${w}%` }} />
    </div>
  );
}

/* ─────────────────────────  MAIN COMPONENT  ───────────────────────── */

export default function AccountsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardView, setDashboardView] = useState<string>('fleet');
  const [activeTab, setActiveTab] = useState<string>('Overview');

  // Financial Overview states
  const [overviewData, setOverviewData] = useState<any>(null);
  const [isOverviewLoading, setIsOverviewLoading] = useState(true);

  // Fleet Budgeting states
  const [fleetTab, setFleetTab] = useState<string>('fleet');
  const [vesselModal, setVesselModal] = useState<any>(null);

  const [ACCOUNT_CODES, setAccountCodes] = useState<any[]>([]);
  const [rawVessels, setRawVessels] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const { state: globalState, vessels: globalVessels } = useGlobalFilter();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = selectedYear === currentYear ? now.getMonth() : (selectedYear < currentYear ? 11 : 0);
  
  let timeFraction = 0;
  let elapsedDays = 0;
  if (selectedYear < currentYear) {
    const isLeap = (selectedYear % 4 === 0 && selectedYear % 100 !== 0) || (selectedYear % 400 === 0);
    elapsedDays = isLeap ? 366 : 365;
    timeFraction = 1;
  } else if (selectedYear === currentYear) {
    const start = new Date(currentYear, 0, 1);
    elapsedDays = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const isLeap = (currentYear % 4 === 0 && currentYear % 100 !== 0) || (currentYear % 400 === 0);
    const daysInYear = isLeap ? 366 : 365;
    timeFraction = elapsedDays / daysInYear;
  } else {
    timeFraction = 0;
    elapsedDays = 0;
  }

  const fetchFinancialOverview = async () => {
    setIsOverviewLoading(true);
    try {
      const params = new URLSearchParams();
      if (globalState.company) {
        params.append('companyId', globalState.company.id.toString());
      }
      if (globalState.vessel) {
        params.append('vesselId', globalState.vessel.id.toString());
      } else if (globalState.company && globalVessels.length > 0) {
        globalVessels.forEach((v: any) => params.append('vesselId', v.id.toString()));
      }
      const res = await api.get(`/accounts/dashboard/financial?${params.toString()}`);
      setOverviewData(res.data?.data);
    } catch (err) {
      console.error('Failed to fetch financial overview:', err);
    } finally {
      setIsOverviewLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialOverview();
    const params = new URLSearchParams();
    params.append('year', selectedYear.toString());
    if (globalState.company) {
      params.append('companyId', globalState.company.id.toString());
    }
    if (globalState.vessel) {
      params.append('vesselId', globalState.vessel.id.toString());
    } else if (globalState.company && globalVessels.length > 0) {
      globalVessels.forEach((v: any) => params.append('vesselId', v.id.toString()));
    }
    
    api.get(`/accounts/budgets/dashboard/summary?${params.toString()}`)
      .then(res => {
        const data = res.data;
        if (data.accountCodes) setAccountCodes(data.accountCodes);
        if (data.vessels) {
          let vs = data.vessels;
          if (globalState.vessel) {
            vs = vs.filter((v: any) => String(v.id) === String(globalState.vessel!.id) || String(v.vesselId) === String(globalState.vessel!.id));
          } else if (globalState.company && globalVessels.length > 0) {
            const validVesselIds = new Set(globalVessels.map((v: any) => String(v.id)));
            vs = vs.filter((v: any) => validVesselIds.has(String(v.id)) || validVesselIds.has(String(v.vesselId)));
          }
          setRawVessels(vs.map((v: any) => ({
            ...v,
            operatingDays: elapsedDays || 1,
            operatingHours: elapsedDays * 24,
            monthlySpend: v.monthlySpend || Array(12).fill(0)
          })));
        }
      })
      .catch(err => console.error("Error fetching dashboard data:", err));
  }, [selectedYear, currentMonthIndex, globalState.company, globalState.vessel]);

  const vessels = enrichVessels(rawVessels, timeFraction).sort((a, b) => b.variance - a.variance);
  const totalBudget = vessels.reduce((s, v) => s + v.allocated, 0);
  const totalUtilized = vessels.reduce((s, v) => s + v.utilized, 0);
  const totalRemaining = totalBudget - totalUtilized;
  const totalVariance = totalUtilized - (totalBudget * timeFraction);
  const totalFac = vessels.reduce((s, v) => s + v.fac, 0);
  const overbudgetCount = vessels.filter(v => v.variance > 0).length;
  const topOverbudgetVessels = vessels.filter(v => v.variance > 0).slice(0, 3);
  const highRiskCodes = ACCOUNT_CODES.filter(ac => ac.utilization > 90).length;
  const utilizationPct = totalBudget > 0 ? (totalUtilized / totalBudget) * 100 : 0;
  const timePct = timeFraction * 100;

  // ─── Dynamic Alerts ───
  const ALERTS = useMemo(() => {
    const alerts: { id: number; severity: string; icon: string; title: string; desc: string; vessel: string; time: string }[] = [];
    let nextId = 1;
    const remainingMonths = 12 - currentMonthIndex - 1;

    // 1. Forecast Breach — vessels projected to exceed annual budget
    vessels.forEach(v => {
      if (v.fac > v.allocated && v.allocated > 0) {
        const overspend = v.fac - v.allocated;
        alerts.push({
          id: nextId++,
          severity: 'critical',
          icon: 'trending-up',
          title: `${v.name} projected to exceed annual budget`,
          desc: `FAC of ${fmt(Math.round(v.fac))} exceeds allocated ${fmt(v.allocated)}. Projected overspend: ${fmt(Math.round(overspend))}.`,
          vessel: v.name,
          time: 'Current'
        });
      }
    });

    // 2. Account Code utilization per vessel (heatmap-based)
    vessels.forEach(v => {
      if (!v.heatmap || !Array.isArray(v.heatmap)) return;
      v.heatmap.forEach((util: number, idx: number) => {
        const ac = ACCOUNT_CODES[idx];
        if (!ac) return;
        if (util >= 95) {
          alerts.push({
            id: nextId++,
            severity: 'critical',
            icon: 'alert-triangle',
            title: `${ac.name} budget critical on ${v.name}`,
            desc: `Account ${ac.code} at ${util.toFixed(0)}% utilization with ${remainingMonths} months remaining.`,
            vessel: v.name,
            time: 'Current'
          });
        } else if (util >= 85) {
          alerts.push({
            id: nextId++,
            severity: 'warning',
            icon: 'alert-circle',
            title: `${ac.name} exceeded 85% on ${v.name}`,
            desc: `Account ${ac.code} at ${util.toFixed(0)}% utilization. Monitor closely for remaining ${remainingMonths} months.`,
            vessel: v.name,
            time: 'Current'
          });
        }
      });
    });

    // 3. Spend spikes — current month vs 3-month trailing average
    vessels.forEach(v => {
      if (!v.monthlySpend || currentMonthIndex < 3) return;
      const currentSpend = v.monthlySpend[currentMonthIndex] || 0;
      const prev3 = v.monthlySpend.slice(currentMonthIndex - 3, currentMonthIndex).filter((s: number) => s > 0);
      if (prev3.length === 0 || currentSpend <= 0) return;
      const avg3 = prev3.reduce((a: number, b: number) => a + b, 0) / prev3.length;
      if (avg3 > 0 && currentSpend > avg3 * 1.4) {
        const spikePct = ((currentSpend - avg3) / avg3) * 100;
        alerts.push({
          id: nextId++,
          severity: 'warning',
          icon: 'zap',
          title: `Spend spike +${spikePct.toFixed(0)}% on ${v.name}`,
          desc: `${MONTH_LABELS[currentMonthIndex]} expense ${fmt(Math.round(currentSpend))} vs 3-month avg ${fmt(Math.round(avg3))}.`,
          vessel: v.name,
          time: 'Current'
        });
      }
    });

    // 4. Healthy budgets — fleet-wide account codes under 40%
    ACCOUNT_CODES.forEach(ac => {
      if (ac.utilization > 0 && ac.utilization < 40 && ac.fleetAllocated > 0) {
        alerts.push({
          id: nextId++,
          severity: 'info',
          icon: 'info',
          title: `${ac.name} budget healthy across fleet`,
          desc: `Fleet-wide utilization at ${ac.utilization.toFixed(0)}%. No action required.`,
          vessel: 'Fleet-wide',
          time: 'Current'
        });
      }
    });

    // Sort: critical first, then warning, then info
    const order = { critical: 0, warning: 1, info: 2 };
    alerts.sort((a, b) => (order[a.severity as keyof typeof order] ?? 3) - (order[b.severity as keyof typeof order] ?? 3));
    return alerts;
  }, [vessels, ACCOUNT_CODES, currentMonthIndex]);

  // Fleet monthly aggregates for trend chart
  const fleetMonthlyBudget = Array.from({ length: 12 }, (_, i) => totalBudget / 12 * (i + 1));
  const fleetMonthlyActual = MONTH_LABELS.map((_, i) => vessels.reduce((s, v) => s + (v.monthlySpend[i] || 0), 0));
  const fleetMonthlyCumulative = fleetMonthlyActual.reduce((acc: number[], val: number) => {
    acc.push((acc.length ? acc[acc.length - 1] : 0) + val);
    return acc;
  }, []);

  const isFinancialView = dashboardView === 'financial';
  const isFleetView = dashboardView === 'fleet';

  useEffect(() => {
    if ((window as any).lucide) {
      (window as any).lucide.createIcons();
    }
  }, [dashboardView, activeTab, fleetTab, vesselModal]);

  const fleetTabs = [
    { key: 'fleet', label: 'Fleet Overview', icon: 'layout-dashboard' },
    { key: 'vessels', label: 'Vessel Analytics', icon: 'ship' },
    { key: 'accounts', label: 'Account Codes', icon: 'grid-3x3' },
    { key: 'forecasts', label: 'Forecasts & Alerts', icon: 'bell-ring' },
  ];

  /* ─── Heatmap color helper ─── */
  const heatColor = (val) => {
    if (val >= 90) return 'bg-red-500 text-white';
    if (val >= 75) return 'bg-red-300 text-red-900';
    if (val >= 60) return 'bg-amber-300 text-amber-900';
    if (val >= 40) return 'bg-amber-100 text-amber-800';
    return 'bg-emerald-100 text-emerald-800';
  };

  const healthColor = (label) => {
    if (label === 'Red') return 'bg-red-100 text-red-700 border-red-200';
    if (label === 'Amber') return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  };

  const healthDot = (label) => {
    if (label === 'Red') return 'bg-red-500';
    if (label === 'Amber') return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const severityStyle = (sev) => {
    if (sev === 'critical') return { border: 'border-l-red-500', bg: 'bg-red-50', icon: 'text-red-600', badge: 'bg-red-100 text-red-700' };
    if (sev === 'warning') return { border: 'border-l-amber-500', bg: 'bg-amber-50', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' };
    return { border: 'border-l-blue-500', bg: 'bg-blue-50', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' };
  };

  /* ─── SVG Trend Chart ─── */
  const renderTrendChart = () => {
    // Build recharts data from existing arrays
    const chartData = MONTH_LABELS.map((month, i) => {
      const entry: any = { name: month, budget: fleetMonthlyBudget[i] };
      if (i <= currentMonthIndex) {
        entry.actual = fleetMonthlyCumulative[i];
      }
      return entry;
    });

    // Add forecast data from current month onward
    const lastActual = fleetMonthlyCumulative[currentMonthIndex];
    const monthlyAvg = lastActual / (currentMonthIndex + 1);
    for (let i = currentMonthIndex; i < 12; i++) {
      chartData[i].forecast = lastActual + monthlyAvg * (i - currentMonthIndex);
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 15, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={(val: number) => fmt(val)} />
          <RTooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)', fontSize: '12px' }} formatter={(value: any) => formatCurrency(Number(value))} />
          <Line type="monotone" dataKey="budget" name="Budget Plan" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />
          <Line type="monotone" dataKey="actual" name="Actual Spend" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3, fill: '#6366f1', stroke: 'white', strokeWidth: 1.5 }} activeDot={{ r: 5 }} connectNulls={false} />
          <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const quickActions = [
    { icon: Plus, title: 'New Invoice', desc: 'Create sales invoice', link: '/accounts/sales/invoices/new', color: 'text-orange-500', bg: 'bg-orange-50' },
    { icon: FileText, title: 'Record Expense', desc: 'Log a new expense', link: '/accounts/purchase/expenses/new', color: 'text-blue-500', bg: 'bg-blue-50' },
    { icon: UserPlus, title: 'Add Customer', desc: 'New customer profile', link: '/accounts/sales/customers/new', color: 'text-purple-500', bg: 'bg-purple-50' },
    { icon: CheckCircle, title: 'Approve Bills', desc: 'Review pending bills', link: '/accounts/purchase/bills', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  ];

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-[#f4f7fc]">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>

        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          

          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 pb-24 md:pb-28" id="main-scroll">



            {/* ════════════════  VIEW: FINANCIAL HUB  ════════════════ */}
            {dashboardView === 'financial' && (
              <>
                {/* Top Bar / Filters */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-4">
                    {/* View Switcher Tabs */}
                    <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 shadow-inner mr-2">
                      <button
                        onClick={() => setDashboardView('financial')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${isFinancialView
                          ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-white'
                          }`}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Accounts Overview
                      </button>
                      <button
                        onClick={() => setDashboardView('fleet')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${isFleetView
                          ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/20'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-white'
                          }`}
                      >
                        <Ship className="w-4 h-4" />
                        Budgets Dashboard
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Global filters are now managed in the main Header */}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="text" placeholder="Search invoices, bills..." className="pl-9 pr-4 py-1.5 bg-slate-50 border-none rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-100 w-64" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col xl:flex-row gap-6">

                  {/* Main Content Area */}
                  <div className="flex-1 space-y-6 overflow-hidden">

                    {/* Quick Actions */}
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h2>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-2">
                        {quickActions.map((action, idx) => (
                          <Link key={idx} to={action.link} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col items-center justify-center text-center hover:shadow-md transition-all group">
                            <div className={`w-12 h-12 rounded-full ${action.bg} ${action.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                              <action.icon className="w-5 h-5" />
                            </div>
                            <h3 className="text-sm font-semibold text-slate-800">{action.title}</h3>
                            <p className="text-[11px] text-slate-500 mt-1">{action.desc}</p>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Metric Cards */}
                    {isOverviewLoading ? (
                      <div className="flex items-center justify-center p-12">
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                      </div>
                    ) : overviewData && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm relative overflow-hidden group">
                          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Unpaid Invoices</div>
                          <div className="text-3xl font-bold text-slate-900 mb-1">{overviewData.quickStats.unpaidInvoices.count}</div>
                          <div className="text-xs text-red-500 flex items-center gap-1">Total {formatCurrency(overviewData.quickStats.unpaidInvoices.amount)}</div>
                          <svg className="absolute bottom-0 left-0 w-full h-12 text-red-100 group-hover:text-red-200 transition-colors" preserveAspectRatio="none" viewBox="0 0 100 20">
                            <path d="M0,20 L10,15 L20,18 L30,10 L40,12 L50,5 L60,8 L70,2 L80,6 L90,1 L100,5 L100,20 Z" fill="currentColor" />
                            <path d="M0,20 L10,15 L20,18 L30,10 L40,12 L50,5 L60,8 L70,2 L80,6 L90,1 L100,5" fill="none" stroke="#ef4444" strokeWidth="1" />
                          </svg>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm relative overflow-hidden group">
                          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Pending Approvals</div>
                          <div className="text-3xl font-bold text-slate-900 mb-1">{overviewData.quickStats.pendingApprovals.count}</div>
                          <div className="text-xs text-emerald-500 flex items-center gap-1">Draft Bills</div>
                          <svg className="absolute bottom-0 left-0 w-full h-12 text-emerald-100 group-hover:text-emerald-200 transition-colors" preserveAspectRatio="none" viewBox="0 0 100 20">
                            <path d="M0,20 L10,18 L20,15 L30,16 L40,10 L50,12 L60,8 L70,10 L80,5 L90,6 L100,2 L100,20 Z" fill="currentColor" />
                            <path d="M0,20 L10,18 L20,15 L30,16 L40,10 L50,12 L60,8 L70,10 L80,5 L90,6 L100,2" fill="none" stroke="#10b981" strokeWidth="1" />
                          </svg>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm relative overflow-hidden group">
                          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Total Revenue (YTD)</div>
                          <div className="text-3xl font-bold text-slate-900 mb-1">{formatCurrency(overviewData.quickStats.totalRevenueYtd.amount)}</div>
                          <div className="text-xs text-emerald-500 flex items-center gap-1">Total Billed</div>
                          <svg className="absolute bottom-0 left-0 w-full h-12 text-blue-100 group-hover:text-blue-200 transition-colors" preserveAspectRatio="none" viewBox="0 0 100 20">
                            <path d="M0,20 L10,18 L20,16 L30,14 L40,15 L50,10 L60,8 L70,6 L80,4 L90,2 L100,1 L100,20 Z" fill="currentColor" />
                            <path d="M0,20 L10,18 L20,16 L30,14 L40,15 L50,10 L60,8 L70,6 L80,4 L90,2 L100,1" fill="none" stroke="#3b82f6" strokeWidth="1" />
                          </svg>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm relative overflow-hidden group">
                          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Expenses (This Month)</div>
                          <div className="text-3xl font-bold text-slate-900 mb-1">{formatCurrency(overviewData.quickStats.expensesThisMonth.amount)}</div>
                          <div className="text-xs text-red-500 flex items-center gap-1">Recent Bills</div>
                          {/* Bar chart representation */}
                          <div className="absolute bottom-4 left-5 right-5 h-8 flex items-end justify-between gap-1 opacity-40 group-hover:opacity-60 transition-opacity">
                            <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: '30%' }}></div>
                            <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: '50%' }}></div>
                            <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: '40%' }}></div>
                            <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: '70%' }}></div>
                            <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: '60%' }}></div>
                            <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: '90%' }}></div>
                            <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: '80%' }}></div>
                            <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: '100%' }}></div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Insights Section */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-slate-50/50">
                        <h2 className="text-lg font-bold text-slate-900">Insights</h2>
                        <div className="flex items-center gap-2">
                          <SearchableSelect className="text-xs border border-slate-200 bg-white rounded-lg px-2 py-1 outline-none text-slate-600">
                            <option>All Accounts</option>
                          </SearchableSelect>
                          <button className="p-1 text-slate-400 hover:text-slate-600"><i data-lucide="refresh-cw" className="w-4 h-4"></i></button>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="flex overflow-x-auto border-b border-slate-200 px-6 hide-scrollbar">
                        {['Overview', 'Cash Flow', 'Revenue Analysis', 'Expense Breakdown', 'Tax Summary'].map(tab => (
                          <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                          >
                            {tab}
                          </button>
                        ))}
                      </div>

                      <div className="p-6 overflow-x-auto">
                        {/* ── Overview Tab ── */}
                        {activeTab === 'Overview' && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 min-w-175">
                            {/* Doughnut Chart */}
                            <div className="flex flex-col">
                              <h3 className="text-sm font-bold text-slate-800 mb-4">Invoice Status</h3>
                              <div className="flex items-center gap-6">
                                <div className="relative w-32 h-32 shrink-0">
                                  <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                                    <path className="text-slate-100" strokeWidth="4" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    <path className="text-emerald-500" strokeDasharray={`${overviewData?.invoiceStatus?.paid / Math.max(overviewData?.invoiceStatus?.total, 1) * 100 || 0}, 100`} strokeWidth="4" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    <path className="text-blue-500" strokeDasharray={`${overviewData?.invoiceStatus?.pending / Math.max(overviewData?.invoiceStatus?.total, 1) * 100 || 0}, 100`} strokeDashoffset={`-${overviewData?.invoiceStatus?.paid / Math.max(overviewData?.invoiceStatus?.total, 1) * 100 || 0}`} strokeWidth="4" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    <path className="text-red-500" strokeDasharray={`${overviewData?.invoiceStatus?.overdue / Math.max(overviewData?.invoiceStatus?.total, 1) * 100 || 0}, 100`} strokeDashoffset={`-${(overviewData?.invoiceStatus?.paid + overviewData?.invoiceStatus?.pending) / Math.max(overviewData?.invoiceStatus?.total, 1) * 100 || 0}`} strokeWidth="4" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                  </svg>
                                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-xl font-bold text-slate-900">{overviewData?.invoiceStatus?.total || 0}</span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">Total</span>
                                  </div>
                                </div>
                                <div className="space-y-3 flex-1">
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div><span className="text-slate-600">Paid</span></div>
                                    <div className="font-semibold text-slate-900">{overviewData?.invoiceStatus?.paid || 0} <span className="text-slate-400 font-normal text-xs ml-1">({Math.round((overviewData?.invoiceStatus?.paid / Math.max(overviewData?.invoiceStatus?.total, 1)) * 100) || 0}%)</span></div>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div><span className="text-slate-600">Pending</span></div>
                                    <div className="font-semibold text-slate-900">{overviewData?.invoiceStatus?.pending || 0} <span className="text-slate-400 font-normal text-xs ml-1">({Math.round((overviewData?.invoiceStatus?.pending / Math.max(overviewData?.invoiceStatus?.total, 1)) * 100) || 0}%)</span></div>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-red-500"></div><span className="text-slate-600">Overdue</span></div>
                                    <div className="font-semibold text-slate-900">{overviewData?.invoiceStatus?.overdue || 0} <span className="text-slate-400 font-normal text-xs ml-1">({Math.round((overviewData?.invoiceStatus?.overdue / Math.max(overviewData?.invoiceStatus?.total, 1)) * 100) || 0}%)</span></div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Cash Flow Trend (SVG) */}
                            <div className="flex flex-col md:col-span-2 md:border-l md:border-slate-100 md:pl-8">
                              <h3 className="text-sm font-bold text-slate-800 mb-4">Cash Flow Trend</h3>
                              <div className="h-48 w-full mt-auto">
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={overviewData?.cashFlow || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                    <YAxis hide domain={['auto', 'auto']} />
                                    <RTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} formatter={(value: any) => formatCurrency(Number(value))} />
                                    <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                    <Line type="monotone" dataKey="inflow" name="Inflow" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                    <Line type="monotone" dataKey="outflow" name="Outflow" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ── Cash Flow Tab ── */}
                        {activeTab === 'Cash Flow' && (
                          <div className="min-w-175">
                            <h3 className="text-sm font-bold text-slate-800 mb-4">Cash Flow Analysis (Last 6 Months)</h3>
                            {(overviewData?.cashFlow?.length) ? (
                              <div className="h-72 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={overviewData.cashFlow} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(val: number) => `$${val >= 1000 ? (val/1000).toFixed(0) + 'k' : val}`} />
                                    <RTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} formatter={(value: any) => formatCurrency(Number(value))} />
                                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                                    <Line type="monotone" dataKey="inflow" name="Total Inflow" stroke="#10b981" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="outflow" name="Total Outflow" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="text-center text-slate-400 py-12 text-sm">No cash flow data available</div>
                            )}
                          </div>
                        )}

                        {/* ── Revenue Analysis Tab ── */}
                        {activeTab === 'Revenue Analysis' && (
                          <div className="min-w-175">
                            <h3 className="text-sm font-bold text-slate-800 mb-4">Top Customers by Revenue (YTD)</h3>
                            {(overviewData?.revenueAnalysis?.length) ? (
                              <div className="h-72 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={overviewData.revenueAnalysis} layout="vertical" margin={{ top: 10, right: 30, left: 100, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                                    <XAxis type="number" hide />
                                    <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#475569' }} />
                                    <RTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} formatter={(value: any) => formatCurrency(Number(value))} />
                                    <Bar dataKey="value" name="Revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={24} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="text-center text-slate-400 py-12 text-sm">No revenue data available</div>
                            )}
                          </div>
                        )}

                        {/* ── Expense Breakdown Tab ── */}
                        {activeTab === 'Expense Breakdown' && (
                          <div className="min-w-175">
                            <h3 className="text-sm font-bold text-slate-800 mb-4">Top Vendors by Expense (YTD)</h3>
                            {(overviewData?.expenseBreakdown?.length) ? (
                              <div className="h-72 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={overviewData.expenseBreakdown} layout="vertical" margin={{ top: 10, right: 30, left: 100, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                                    <XAxis type="number" hide />
                                    <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#475569' }} />
                                    <RTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} formatter={(value: any) => formatCurrency(Number(value))} />
                                    <Bar dataKey="value" name="Total Paid" fill="#ef4444" radius={[0, 4, 4, 0]} barSize={24} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="text-center text-slate-400 py-12 text-sm">No expense data available</div>
                            )}
                          </div>
                        )}

                        {/* ── Tax Summary Tab ── */}
                        {activeTab === 'Tax Summary' && (
                          <div className="min-w-175">
                            <h3 className="text-sm font-bold text-slate-800 mb-6">YTD Tax Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600"><i data-lucide="arrow-down-to-line" className="w-4 h-4"></i></div>
                                  <div className="text-sm font-semibold text-emerald-800">Tax Collected</div>
                                </div>
                                <div className="text-3xl font-bold text-emerald-600">{formatCurrency(overviewData?.taxSummary?.collected || 0)}</div>
                                <p className="text-xs text-emerald-600/70 mt-2">From customer invoices</p>
                              </div>
                              <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600"><i data-lucide="arrow-up-from-line" className="w-4 h-4"></i></div>
                                  <div className="text-sm font-semibold text-blue-800">Tax Paid</div>
                                </div>
                                <div className="text-3xl font-bold text-blue-600">{formatCurrency(overviewData?.taxSummary?.paid || 0)}</div>
                                <p className="text-xs text-blue-600/70 mt-2">To vendors and suppliers</p>
                              </div>
                              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600"><i data-lucide="scale" className="w-4 h-4"></i></div>
                                  <div className="text-sm font-semibold text-slate-800">Net Liability</div>
                                </div>
                                <div className="text-3xl font-bold text-slate-900">{formatCurrency(overviewData?.taxSummary?.netLiability || 0)}</div>
                                <p className="text-xs text-slate-500 mt-2">Difference (Collected - Paid)</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tables Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-20">
                      {/* Table 1 */}
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 bg-slate-50/50">
                          <h3 className="text-sm font-bold text-slate-800">Top Unpaid Invoices</h3>
                          <button onClick={() => navigate('/accounts/sales/invoices')} className="text-xs text-blue-600 hover:text-blue-700 font-medium">View all &rarr;</button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="text-[10px] uppercase text-slate-500 bg-slate-50/30">
                              <tr>
                                <th className="px-5 py-3 font-semibold">Customer</th>
                                <th className="px-5 py-3 font-semibold">Inv #</th>
                                <th className="px-5 py-3 font-semibold text-right">Amount</th>
                                <th className="px-5 py-3 font-semibold">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {overviewData?.topUnpaidInvoices?.length ? overviewData.topUnpaidInvoices.map((row: any, i: number) => (
                                <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="px-5 py-3 font-medium text-slate-800">{row.customer}</td>
                                  <td className="px-5 py-3 text-slate-500">{row.invoice_number}</td>
                                  <td className="px-5 py-3 text-right font-medium text-slate-700">{formatCurrency(row.balance)}</td>
                                  <td className="px-5 py-3">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${row.status?.toLowerCase() === 'overdue'
                                        ? 'bg-red-50 text-red-600 border-red-100'
                                        : 'bg-amber-50 text-amber-600 border-amber-100'
                                      }`}>
                                      {row.status}
                                    </span>
                                  </td>
                                </tr>
                              )) : (
                                <tr>
                                  <td colSpan={4} className="px-5 py-8 text-center text-slate-400 text-sm">No unpaid invoices</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Table 2 */}
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 bg-slate-50/50">
                          <h3 className="text-sm font-bold text-slate-800">Recent Purchase Orders</h3>
                          <button onClick={() => navigate('/accounts/purchase/purchase-orders')} className="text-xs text-blue-600 hover:text-blue-700 font-medium">View all &rarr;</button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="text-[10px] uppercase text-slate-500 bg-slate-50/30">
                              <tr>
                                <th className="px-5 py-3 font-semibold">PO No.</th>
                                <th className="px-5 py-3 font-semibold">Vendor</th>
                                <th className="px-5 py-3 font-semibold text-right">Amount</th>
                                <th className="px-5 py-3 font-semibold">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {overviewData?.recentPOs?.length ? overviewData.recentPOs.map((row: any, i: number) => {
                                const statusLower = row.status?.toLowerCase() || '';
                                let color = 'bg-slate-50 text-slate-600 border-slate-100';
                                if (statusLower === 'open') color = 'bg-emerald-50 text-emerald-600 border-emerald-100';
                                if (statusLower === 'received') color = 'bg-blue-50 text-blue-600 border-blue-100';
                                if (statusLower === 'partially received') color = 'bg-amber-50 text-amber-600 border-amber-100';

                                return (
                                  <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-5 py-3 text-slate-500">{row.po_number}</td>
                                    <td className="px-5 py-3 font-medium text-slate-800">{row.vendor}</td>
                                    <td className="px-5 py-3 text-right font-medium text-slate-700">{formatCurrency(row.amount)}</td>
                                    <td className="px-5 py-3">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${color}`}>
                                        {row.status || 'Draft'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              }) : (
                                <tr>
                                  <td colSpan={4} className="px-5 py-8 text-center text-slate-400 text-sm">No recent POs</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Sidebar (Assistant) - commented out for now
              <div className="w-full xl:w-80 shrink-0 space-y-4 pb-20">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 xl:sticky xl:top-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white">
                      <i data-lucide="bot" className="w-5 h-5"></i>
                    </div>
                    <h2 className="text-base font-bold text-slate-900">Accounts Assistant</h2>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">Suggested prompts</p>
                  <div className="space-y-2 mb-6">
                    <button className="w-full text-left px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm text-slate-700 transition-colors">Show overdue invoices for Oceanic Supplies</button>
                    <button className="w-full text-left px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm text-slate-700 transition-colors">Summarize expenses for May 2024</button>
                    <button className="w-full text-left px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm text-slate-700 transition-colors">Which vendor has the highest unpaid balance?</button>
                    <button className="w-full text-left px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm text-slate-700 transition-colors">Draft a reminder email for overdue accounts</button>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4 relative">
                    <div className="absolute top-3 right-3 text-blue-300">
                      <i data-lucide="message-square" className="w-4 h-4"></i>
                    </div>
                    <div className="text-xs font-semibold text-blue-800 mb-1">Show overdue invoices...</div>
                    <div className="text-xs text-blue-600 mt-2 bg-white/60 p-2 rounded-lg">
                      Here are the 4 overdue invoices for Oceanic Supplies totaling $12,450. The oldest is INV-2024-052, overdue by 12 days.
                    </div>
                  </div>

                  <div className="relative">
                    <input type="text" placeholder="Ask something..." className="w-full pl-4 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all shadow-sm" />
                    <button className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center transition-colors">
                      <i data-lucide="send" className="w-4 h-4"></i>
                    </button>
                  </div>
                  <div className="text-center mt-3">
                    <span className="text-[10px] text-slate-400">AI responses may be inaccurate. Please verify.</span>
                  </div>
                </div>
              </div>
              */}

                </div>
              </>
            )}

            {/* ════════════════  VIEW: FLEET BUDGETING  ════════════════ */}
            {dashboardView === 'fleet' && (
              <>
                {/* ─── Header Bar ─── */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
                  <div className="flex items-center gap-4">
                    {/* View Switcher Tabs */}
                    <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                      <button
                        onClick={() => setDashboardView('financial')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${isFinancialView
                          ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                          }`}
                      >
                        <i data-lucide="layout-dashboard" className="w-4 h-4"></i>
                        Accounts Overview
                      </button>
                      <button
                        onClick={() => setDashboardView('fleet')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${isFleetView
                          ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                          }`}
                      >
                        <i data-lucide="ship" className="w-4 h-4"></i>
                        Budget Dashboard
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600 bg-white px-3 py-2 rounded-xl border border-slate-200 shadow-sm">
                      <i data-lucide="calendar" className="w-4 h-4 text-indigo-500"></i>
                      <SearchableSelect
                        className="font-medium bg-transparent outline-none cursor-pointer"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                      >
                        {[2024, 2025, 2026, 2027].map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </SearchableSelect>
                      <span className="text-[10px] text-slate-400 ml-1">{currentMonthIndex + 1} of 12 months</span>
                    </div>
                    <button className="p-2 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-indigo-600 hover:border-indigo-200 transition-colors shadow-sm">
                      <i data-lucide="download" className="w-4 h-4"></i>
                    </button>
                  </div>
                </div>

                {/* ─── Fleet Tab Navigation ─── */}
                <div className="flex items-center gap-1 mb-6 bg-white p-1 rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                  {fleetTabs.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setFleetTab(tab.key)}
                      className={`flex items-center justify-center flex-1 gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${fleetTab === tab.key
                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                      <i data-lucide={tab.icon} className="w-4 h-4"></i>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* ════════════════  TAB: FLEET OVERVIEW  ════════════════ */}
                {fleetTab === 'fleet' && (
                  <div className="flex flex-col xl:flex-row gap-6 pb-20">
                    <div className="flex-1 space-y-6 overflow-hidden">

                      {/* KPI Row */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

                        {/* Total Fleet Budget */}
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Fleet Budget</div>
                            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                              <i data-lucide="wallet" className="w-4 h-4 text-indigo-600"></i>
                            </div>
                          </div>
                          <div className="text-2xl font-black text-slate-900 tracking-tight">{fmt(totalBudget)}</div>
                          <div className="text-xs text-slate-500 mt-1">Annual allocation • {vessels.length} vessels</div>
                        </div>

                        {/* Budget Utilized */}
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Budget Utilized</div>
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                              <i data-lucide="bar-chart-3" className="w-4 h-4 text-blue-600"></i>
                            </div>
                          </div>
                          <div className="text-2xl font-black text-slate-900 tracking-tight">{fmt(totalUtilized)}</div>
                          <div className="mt-2">
                            <MiniProgress value={utilizationPct} color={utilizationPct > timePct ? 'bg-red-500' : 'bg-blue-500'} />
                          </div>
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-xs text-slate-500">{pct(utilizationPct)} spent</span>
                            <span className="text-xs text-slate-400">{pct(timePct)} of year</span>
                          </div>
                        </div>

                        {/* Remaining Budget */}
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Remaining Budget</div>
                            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                              <i data-lucide="piggy-bank" className="w-4 h-4 text-emerald-600"></i>
                            </div>
                          </div>
                          <div className="text-2xl font-black text-emerald-700 tracking-tight">{fmt(totalRemaining)}</div>
                          <div className="text-xs text-slate-500 mt-1"></div>
                        </div>

                        {/* Budget Variance */}
                        <div className={`bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition-shadow group ${totalVariance > 0 ? 'border-red-200' : 'border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Budget Variance</div>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform ${totalVariance > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                              <i data-lucide={totalVariance > 0 ? 'trending-up' : 'trending-down'} className={`w-4 h-4 ${totalVariance > 0 ? 'text-red-600' : 'text-emerald-600'}`}></i>
                            </div>
                          </div>
                          <div className={`text-2xl font-black tracking-tight ${totalVariance > 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                            {totalVariance > 0 ? '+' : ''}{fmt(Math.abs(totalVariance))}
                          </div>
                          <div className="text-xs mt-1">
                            <span className={totalVariance > 0 ? 'text-red-500' : 'text-emerald-500'}>
                              {totalVariance > 0 ? 'Over budget for elapsed period' : 'Under budget for elapsed period'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Second KPI Row */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

                        {/* Forecast at Completion */}
                        <div className={`bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition-shadow group ${totalFac > totalBudget ? 'border-red-200' : 'border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Forecast at Completion</div>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform ${totalFac > totalBudget ? 'bg-red-50' : 'bg-emerald-50'}`}>
                              <i data-lucide="target" className={`w-4 h-4 ${totalFac > totalBudget ? 'text-red-600' : 'text-emerald-600'}`}></i>
                            </div>
                          </div>
                          <div className={`text-2xl font-black tracking-tight ${totalFac > totalBudget ? 'text-red-600' : 'text-slate-900'}`}>{fmt(totalFac)}</div>
                          {totalFac > totalBudget && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">
                                <i data-lucide="alert-triangle" className="w-3 h-3 mr-0.5"></i> BREACH
                              </span>
                              <span className="text-[11px] text-red-500">+{fmt(totalFac - totalBudget)} projected overspend</span>
                            </div>
                          )}
                        </div>

                        {/* Budget Consumption Rate */}
                        <div className={`bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition-shadow group ${utilizationPct > timePct ? 'border-amber-200' : 'border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Consumption Rate</div>
                            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                              <i data-lucide="gauge" className="w-4 h-4 text-amber-600"></i>
                            </div>
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-black text-slate-900 tracking-tight">{pct(utilizationPct)}</span>
                            <span className="text-sm text-slate-400">budget</span>
                          </div>
                          <div className="text-xs mt-1">
                            {utilizationPct > timePct ? (
                              <span className="text-amber-600 font-semibold">⚠ {pct(utilizationPct)} consumed in {pct(timePct)} of year</span>
                            ) : (
                              <span className="text-emerald-600">On track — within time proportion</span>
                            )}
                          </div>
                        </div>

                        {/* Overbudget Vessels */}
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow group cursor-pointer" onClick={() => setFleetTab('vessels')}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Overbudget Vessels</div>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform ${overbudgetCount > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                              <i data-lucide="ship" className={`w-4 h-4 ${overbudgetCount > 0 ? 'text-red-600' : 'text-emerald-600'}`}></i>
                            </div>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className={`text-2xl font-black tracking-tight ${overbudgetCount > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{overbudgetCount}</span>
                            <span className="text-lg text-slate-400 font-medium">/ {vessels.length}</span>
                          </div>
                          <div className="text-xs text-blue-600 font-medium mt-1 group-hover:underline flex items-center gap-1">
                            View vessel details <i data-lucide="arrow-right" className="w-3 h-3"></i>
                          </div>
                        </div>

                        {/* High-Risk Account Codes */}
                        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow group cursor-pointer" onClick={() => setFleetTab('accounts')}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">High-Risk Accounts</div>
                            <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                              <i data-lucide="shield-alert" className="w-4 h-4 text-orange-600"></i>
                            </div>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-black text-orange-600 tracking-tight">{highRiskCodes}</span>
                            <span className="text-sm text-slate-400">codes &gt; 90%</span>
                          </div>
                          <div className="text-xs text-blue-600 font-medium mt-1 group-hover:underline flex items-center gap-1">
                            View heatmap <i data-lucide="arrow-right" className="w-3 h-3"></i>
                          </div>
                        </div>
                      </div>

                      {/* ─── Trend Chart + Top Vessels ─── */}
                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">

                        {/* Budget vs Actual Trend */}
                        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                            <div>
                              <h2 className="text-sm font-bold text-slate-900">Budget vs Actual — Cumulative Fleet Spend</h2>
                              <p className="text-[11px] text-slate-500 mt-0.5">Tracking year-to-date performance against budget allocation</p>
                            </div>
                            <div className="flex items-center gap-4 text-[11px]">
                              <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 bg-slate-400" style={{ borderTop: '2px dashed #94a3b8' }}></div><span className="text-slate-500">Budget Plan</span></div>
                              <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 bg-indigo-500 rounded"></div><span className="text-slate-500">Actual Spend</span></div>
                              <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 bg-amber-500" style={{ borderTop: '2px dashed #f59e0b' }}></div><span className="text-slate-500">Forecast</span></div>
                            </div>
                          </div>
                          <div className="p-6 h-60">
                            {renderTrendChart()}
                          </div>
                        </div>

                        {/* Top Overspending Vessels */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                          <div className="px-5 py-4 border-b border-slate-100">
                            <h2 className="text-sm font-bold text-slate-900">Highest Overspend</h2>
                            <p className="text-[11px] text-slate-500 mt-0.5">Vessels sorted by budget variance</p>
                          </div>
                          <div className="divide-y divide-slate-100">
                            {vessels.slice(0, 5).map((v, i) => (
                              <div key={v.id} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setVesselModal(v)}>
                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${v.variance > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                  {i + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-slate-800 truncate">{v.name}</div>
                                  <div className="text-[11px] text-slate-500">{v.type} • {pct(v.utilizationPct)} used</div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className={`text-sm font-bold ${v.variance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                    {v.variance > 0 ? '+' : ''}{fmt(Math.abs(v.variance))}
                                  </div>
                                  <Sparkline data={v.monthlySpend} color={v.variance > 0 ? '#ef4444' : '#10b981'} width={50} height={18} />
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="px-5 py-3 border-t border-slate-100">
                            <button onClick={() => setFleetTab('vessels')} className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1">
                              View all vessels <i data-lucide="arrow-right" className="w-3 h-3"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* ─── Spend Drivers Snapshot ─── */}
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100">
                          <h2 className="text-sm font-bold text-slate-900">Spend Drivers Snapshot</h2>
                          <p className="text-[11px] text-slate-500 mt-0.5">Largest variances contributing to YTD pressure</p>
                        </div>
                        <div className="p-6 space-y-4">
                          {topOverbudgetVessels.map(v => (
                            <div key={v.id} className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold ${v.utilizationPct > timePct ? 'bg-red-50 text-red-700' : 'bg-indigo-50 text-indigo-700'}`}>
                                {Math.round(v.utilizationPct)}%
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-900 truncate">{v.name}</div>
                                <div className="text-[11px] text-slate-500">{v.type} • {pct(v.utilizationPct)} used</div>
                                <div className="mt-2">
                                  <MiniProgress value={v.utilizationPct} color={v.utilizationPct > timePct ? 'bg-red-500' : 'bg-indigo-500'} />
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-sm font-bold text-red-600">+{fmt(Math.abs(v.variance))}</div>
                                <div className="text-[10px] text-slate-400">variance</div>
                              </div>
                            </div>
                          ))}
                          {topOverbudgetVessels.length === 0 && (
                            <div className="text-sm text-slate-500">No vessels over budget for the elapsed period.</div>
                          )}
                        </div>
                      </div>

                      {/* ─── Recent Alerts Strip ─── */}
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                            <h2 className="text-sm font-bold text-slate-900">Active Exceptions</h2>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">{ALERTS.filter(a => a.severity === 'critical').length} critical</span>
                          </div>
                          <button onClick={() => setFleetTab('forecasts')} className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1">
                            View all <i data-lucide="arrow-right" className="w-3 h-3"></i>
                          </button>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {ALERTS.filter(a => a.severity === 'critical').map(alert => {
                            const style = severityStyle(alert.severity);
                            return (
                              <div key={alert.id} className={`px-6 py-3 flex items-start gap-3 border-l-4 ${style.border} ${style.bg}`}>
                                <i data-lucide={alert.icon} className={`w-4 h-4 mt-0.5 shrink-0 ${style.icon}`}></i>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-slate-800">{alert.title}</div>
                                  <div className="text-[11px] text-slate-500 mt-0.5">{alert.vessel} • {alert.time}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      
                    </div>

                    {/* Right Sidebar (Assistant) - commented out for now
                <div className="w-full xl:w-80 shrink-0 space-y-4">
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 xl:sticky xl:top-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
                        <i data-lucide="bot" className="w-5 h-5"></i>
                      </div>
                      <h2 className="text-base font-bold text-slate-900">Finance Assistant</h2>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">Suggested analysis</p>
                    <div className="space-y-2 mb-6">
                      <button className="w-full text-left px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm text-slate-700 transition-colors">Which vessel has the highest variance?</button>
                      <button className="w-full text-left px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm text-slate-700 transition-colors">Forecast year-end overspend</button>
                      <button className="w-full text-left px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm text-slate-700 transition-colors">Show dry dock costs for Coral Breeze</button>
                    </div>

                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-4 relative">
                      <div className="absolute top-3 right-3 text-indigo-300">
                        <i data-lucide="message-square" className="w-4 h-4"></i>
                      </div>
                      <div className="text-xs font-semibold text-indigo-800 mb-1">Which vessel has the highest...</div>
                      <div className="text-xs text-indigo-600 mt-2 bg-white/60 p-2 rounded-lg">
                        MT Azure Meridian has the highest variance (+$114K), followed by MT Oceanic Titan (+$30K).
                      </div>
                    </div>

                    <div className="relative">
                      <input type="text" placeholder="Ask AI..." className="w-full pl-4 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all shadow-sm" />
                      <button className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center justify-center transition-colors">
                        <i data-lucide="send" className="w-4 h-4"></i>
                      </button>
                    </div>
                    <div className="text-center mt-3">
                      <span className="text-[10px] text-slate-400">AI responses may be inaccurate. Please verify.</span>
                    </div>
                  </div>
                </div>
                */}

                  </div>
                )}

                {/* ════════════════  TAB: VESSEL ANALYTICS  ════════════════ */}
                {fleetTab === 'vessels' && (
                  <div className="space-y-6 pb-20">

                    {/* Maritime Metrics Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Avg Budget Per Day</div>
                        <div className="text-xl font-black text-slate-900">${(vessels.reduce((s, v) => s + v.budgetPerDay, 0) / (vessels.length || 1)).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Across all vessels</div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Avg Cost Per Week</div>
                        <div className="text-xl font-black text-slate-900">${safeNumber(vessels.reduce((s, v) => s + safeNumber(v.costPerWeek), 0) / (vessels.length || 1)).toFixed(2)}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Weekly operating cost</div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Fleet Health</div>
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-black text-slate-900">{safeNumber(vessels.reduce((s, v) => s + safeNumber(v.healthScore), 0) / (vessels.length || 1)).toFixed(0)}</span>
                          <span className="text-sm text-slate-400">/ 100</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Weighted average</div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">FAC Breach Vessels</div>
                        {(() => {
                          const breachCount = vessels.filter(v => v.fac > v.allocated).length;
                          const color = breachCount === 0 ? 'text-emerald-600' : breachCount <= 2 ? 'text-amber-600' : 'text-red-600';
                          const subColor = breachCount === 0 ? 'text-emerald-500' : breachCount <= 2 ? 'text-amber-500' : 'text-red-500';
                          const label = breachCount === 0 ? 'All vessels within budget' : 'Projected to exceed budget';
                          return (
                            <>
                              <div className={`text-xl font-black ${color}`}>{breachCount}</div>
                              <div className={`text-[11px] ${subColor} mt-0.5`}>{label}</div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Vessel Ranking Table */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                        <div>
                          <h2 className="text-sm font-bold text-slate-900">Vessel Budget Ranking</h2>
                          <p className="text-[11px] text-slate-500 mt-0.5">Sorted by highest variance — click any row for details</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left min-w-275">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                              <th className="px-5 py-3 w-8">#</th>
                              <th className="px-5 py-3">Vessel</th>
                              <th className="px-5 py-3">Type</th>
                              <th className="px-5 py-3 text-right">Allocated</th>
                              <th className="px-5 py-3 text-right">Utilized</th>
                              <th className="px-5 py-3 text-right">Remaining</th>
                              <th className="px-5 py-3 text-right">Variance</th>
                              <th className="px-5 py-3 text-right">FAC</th>
                              <th className="px-5 py-3 text-center">Health</th>
                              <th className="px-5 py-3 text-right">$/Day</th>
                              <th className="px-5 py-3 text-right">$/Wk</th>
                              <th className="px-5 py-3 text-center">Trend</th>
                            </tr>
                          </thead>
                          <tbody className="text-sm divide-y divide-slate-100">
                            {vessels.map((v, i) => (
                              <tr key={v.id} className="hover:bg-slate-50 transition-colors cursor-pointer group" onClick={() => setVesselModal(v)}>
                                <td className="px-5 py-3.5 text-slate-400 font-mono text-xs">{i + 1}</td>
                                <td className="px-5 py-3.5">
                                  <div className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">{v.name}</div>
                                </td>
                                <td className="px-5 py-3.5">
                                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600">{v.type}</span>
                                </td>
                                <td className="px-5 py-3.5 text-right font-mono text-slate-700">{fmtFull(v.allocated)}</td>
                                <td className="px-5 py-3.5 text-right font-mono text-slate-800 font-semibold">{fmtFull(v.utilized)}</td>
                                <td className="px-5 py-3.5 text-right font-mono text-emerald-600">{fmtFull(v.remaining)}</td>
                                <td className={`px-5 py-3.5 text-right font-mono font-bold ${v.variance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                  {v.variance > 0 ? '+' : ''}{fmtFull(Math.round(v.variance))}
                                </td>
                                <td className={`px-5 py-3.5 text-right font-mono ${v.fac > v.allocated ? 'text-red-600 font-bold' : 'text-slate-700'}`}>
                                  {fmtFull(Math.round(v.fac))}
                                  {v.fac > v.allocated && <span className="text-[9px] ml-1 text-red-500">▲</span>}
                                </td>
                                <td className="px-5 py-3.5 text-center">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <div className={`w-2 h-2 rounded-full ${healthDot(v.healthLabel)}`}></div>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${healthColor(v.healthLabel)}`}>
                                      {Math.round(v.healthScore)}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-5 py-3.5 text-right font-mono text-slate-600 text-xs">${v.budgetPerDay.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                                <td className="px-5 py-3.5 text-right font-mono text-slate-600 text-xs">${safeNumber(v.costPerWeek).toFixed(2)}</td>
                                <td className="px-5 py-3.5 text-center">
                                  <Sparkline data={v.monthlySpend} color={v.variance > 0 ? '#ef4444' : '#6366f1'} width={60} height={22} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Vessel Type Comparison */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-100">
                        <h2 className="text-sm font-bold text-slate-900">Vessel Type Comparison</h2>
                        <p className="text-[11px] text-slate-500 mt-0.5">Benchmarking similar vessel types against each other</p>
                      </div>
                      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {Array.from(new Set(vessels.map(v => v.type))).filter(Boolean).map(type => {
                          const typeVessels = vessels.filter(v => v.type === type);
                          const avgUtil = typeVessels.reduce((s, v) => s + v.utilizationPct, 0) / (typeVessels.length || 1);
                          const avgPerDay = typeVessels.reduce((s, v) => s + v.budgetPerDay, 0) / (typeVessels.length || 1);
                          const avgPerWeek = typeVessels.reduce((s, v) => s + v.costPerWeek, 0) / (typeVessels.length || 1);
                          const avgHealth = typeVessels.reduce((s, v) => s + v.healthScore, 0) / (typeVessels.length || 1);
                          return (
                            <div key={type as string} className="p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                              <div className="flex items-center gap-2 mb-3">
                                <i data-lucide="ship" className="w-4 h-4 text-indigo-500"></i>
                                <span className="text-sm font-bold text-slate-800">{type as string}</span>
                                <span className="text-[10px] text-slate-400 ml-auto">{typeVessels.length} vessels</span>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <div className="text-slate-500 mb-0.5">Avg Utilization</div>
                                  <div className={`font-bold ${avgUtil > 80 ? 'text-red-600' : 'text-slate-900'}`}>{pct(avgUtil)}</div>
                                </div>
                                <div>
                                  <div className="text-slate-500 mb-0.5">Avg Health</div>
                                  <div className="font-bold text-slate-900">{safeNumber(avgHealth).toFixed(0)}/100</div>
                                </div>
                                <div>
                                  <div className="text-slate-500 mb-0.5">Avg $/Day</div>
                                  <div className="font-bold text-slate-900">${avgPerDay.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                                </div>
                                <div>
                                  <div className="text-slate-500 mb-0.5">Avg $/Wk</div>
                                  <div className="font-bold text-slate-900">${safeNumber(avgPerWeek).toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    
                  </div>
                )}

                {/* ════════════════  TAB: ACCOUNT CODES  ════════════════ */}
                {fleetTab === 'accounts' && (
                  <div className="space-y-6 pb-20">

                    {/* Top Overspending Account Codes */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-100">
                        <h2 className="text-sm font-bold text-slate-900">Top Overspending Account Codes</h2>
                        <p className="text-[11px] text-slate-500 mt-0.5">Fleet-wide account code utilization — sorted by highest risk</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left min-w-200">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                              <th className="px-5 py-3">Account Code</th>
                              <th className="px-5 py-3 text-right">Fleet Allocated</th>
                              <th className="px-5 py-3 text-right">Fleet Actual</th>
                              <th className="px-5 py-3 text-right">Variance %</th>
                              <th className="px-5 py-3">Utilization</th>
                              <th className="px-5 py-3 text-center">Vessels Impacted</th>
                            </tr>
                          </thead>
                          <tbody className="text-sm divide-y divide-slate-100">
                            {ACCOUNT_CODES.map((ac, idx) => {
                              const avgUtil = ac.utilization || 0;
                              const impacted = ac.vesselsImpacted || 0;
                              const allocated = ac.fleetAllocated || 0;
                              const actual = ac.fleetActual || 0;
                              const variancePct = ac.variancePct || 0;
                              return (
                                <tr key={ac.code} className="hover:bg-slate-50 transition-colors">
                                  <td className="px-5 py-3.5">
                                    <div className="font-semibold text-slate-900">{ac.code} — {ac.name}</div>
                                  </td>
                                  <td className="px-5 py-3.5 text-right font-mono text-slate-700">{fmtFull(allocated)}</td>
                                  <td className="px-5 py-3.5 text-right font-mono text-slate-900 font-semibold">{fmtFull(actual)}</td>
                                  <td className={`px-5 py-3.5 text-right font-mono font-bold ${variancePct > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                    {variancePct > 0 ? '+' : ''}{variancePct.toFixed(1)}%
                                  </td>
                                  <td className="px-5 py-3.5">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1">
                                        <MiniProgress value={avgUtil} color={avgUtil > 90 ? 'bg-red-500' : avgUtil > 75 ? 'bg-amber-500' : 'bg-emerald-500'} />
                                      </div>
                                      <span className="text-xs font-mono text-slate-600 w-10 text-right">{avgUtil.toFixed(0)}%</span>
                                    </div>
                                  </td>
                                  <td className="px-5 py-3.5 text-center">
                                    {impacted > 0 ? (
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">{impacted} vessels</span>
                                    ) : (
                                      <span className="text-[11px] text-slate-400">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Account Code Heatmap */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-100">
                        <h2 className="text-sm font-bold text-slate-900">Budget Utilization Heatmap</h2>
                        <p className="text-[11px] text-slate-500 mt-0.5">Vessels × Account Codes — color intensity = utilization %</p>
                      </div>
                      <div className="p-6 overflow-x-auto">
                        <table className="w-full min-w-175">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-slate-400 tracking-wider w-48">Vessel</th>
                              {ACCOUNT_CODES.map(ac => (
                                <th key={ac.code} className="px-2 py-2 text-center text-[10px] uppercase font-bold text-slate-400 tracking-wider">{ac.name}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {vessels.map(v => (
                              <tr key={v.id} className="border-t border-slate-100">
                                <td className="px-3 py-2">
                                  <div className="text-xs font-semibold text-slate-800">{v.name}</div>
                                  <div className="text-[10px] text-slate-400">{v.type}</div>
                                </td>
                                {v.heatmap.map((val: number, hi: number) => (
                                  <td key={hi} className="px-2 py-2 text-center">
                                    <div className={`inline-flex items-center justify-center w-12 h-8 rounded-lg text-[11px] font-bold ${heatColor(val)} transition-all hover:scale-110 cursor-default`} title={`${v.name} — ${ACCOUNT_CODES[hi]?.name}: ${val.toFixed(0)}%`}>
                                      {val.toFixed(0)}%
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Legend */}
                        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Legend:</span>
                          <div className="flex items-center gap-1"><div className="w-5 h-3 rounded bg-emerald-100"></div><span className="text-[10px] text-slate-500">&lt; 40%</span></div>
                          <div className="flex items-center gap-1"><div className="w-5 h-3 rounded bg-amber-100"></div><span className="text-[10px] text-slate-500">40-59%</span></div>
                          <div className="flex items-center gap-1"><div className="w-5 h-3 rounded bg-amber-300"></div><span className="text-[10px] text-slate-500">60-74%</span></div>
                          <div className="flex items-center gap-1"><div className="w-5 h-3 rounded bg-red-300"></div><span className="text-[10px] text-slate-500">75-89%</span></div>
                          <div className="flex items-center gap-1"><div className="w-5 h-3 rounded bg-red-500"></div><span className="text-[10px] text-slate-500">≥ 90%</span></div>
                        </div>
                      </div>
                    </div>

                    
                  </div>
                )}

                {/* ════════════════  TAB: FORECASTS & ALERTS  ════════════════ */}
                {fleetTab === 'forecasts' && (
                  <div className="space-y-6 pb-20">

                    {/* Forecast Breach Prediction */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-100">
                        <h2 className="text-sm font-bold text-slate-900">Forecast Breach Prediction</h2>
                        <p className="text-[11px] text-slate-500 mt-0.5">Vessels projected to exceed annual budget based on current burn rate</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left min-w-200">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                              <th className="px-5 py-3">Vessel</th>
                              <th className="px-5 py-3 text-right">Budget</th>
                              <th className="px-5 py-3 text-right">FAC</th>
                              <th className="px-5 py-3 text-right">Projected Overspend</th>
                              <th className="px-5 py-3">Breach Risk</th>
                              <th className="px-5 py-3 text-right">Monthly Burn</th>
                            </tr>
                          </thead>
                          <tbody className="text-sm divide-y divide-slate-100">
                            {vessels.filter(v => v.fac > v.allocated).sort((a, b) => (b.fac - b.allocated) - (a.fac - a.allocated)).map(v => {
                              const overspend = v.fac - v.allocated;
                              const breachPct = (overspend / v.allocated) * 100;
                              return (
                                <tr key={v.id} className="hover:bg-red-50/30 transition-colors">
                                  <td className="px-5 py-3.5">
                                    <div className="font-semibold text-slate-900">{v.name}</div>
                                    <div className="text-[10px] text-slate-500">{v.type}</div>
                                  </td>
                                  <td className="px-5 py-3.5 text-right font-mono text-slate-700">{fmtFull(v.allocated)}</td>
                                  <td className="px-5 py-3.5 text-right font-mono text-red-600 font-bold">{fmtFull(Math.round(v.fac))}</td>
                                  <td className="px-5 py-3.5 text-right font-mono text-red-600 font-bold">+{fmtFull(Math.round(overspend))}</td>
                                  <td className="px-5 py-3.5">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 max-w-25">
                                        <MiniProgress value={breachPct} max={20} color="bg-red-500" />
                                      </div>
                                      <span className="text-[11px] font-bold text-red-600">{pct(breachPct)}</span>
                                    </div>
                                  </td>
                                  <td className="px-5 py-3.5 text-right font-mono text-slate-600">{fmtFull(Math.round(v.avgMonthlyBurn))}</td>
                                </tr>
                              );
                            })}
                            {vessels.filter(v => v.fac > v.allocated).length === 0 && (
                              <tr>
                                <td colSpan={6} className="px-5 py-12 text-center text-slate-400">
                                  No vessels projected to breach annual budget 🎉
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* All Exception Alerts */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                          <h2 className="text-sm font-bold text-slate-900">Exception Monitor</h2>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">{ALERTS.filter(a => a.severity === 'critical').length} critical</span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">{ALERTS.filter(a => a.severity === 'warning').length} warning</span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">{ALERTS.filter(a => a.severity === 'info').length} info</span>
                        </div>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {ALERTS.map(alert => {
                          const style = severityStyle(alert.severity);
                          return (
                            <div key={alert.id} className={`px-6 py-4 flex items-start gap-4 border-l-4 ${style.border} hover:${style.bg} transition-colors`}>
                              <div className={`w-8 h-8 rounded-lg ${style.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                                <i data-lucide={alert.icon} className={`w-4 h-4 ${style.icon}`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${style.badge}`}>{alert.severity}</span>
                                  <span className="text-[11px] text-slate-400">{alert.time}</span>
                                </div>
                                <div className="text-sm font-semibold text-slate-800">{alert.title}</div>
                                <div className="text-[12px] text-slate-500 mt-1 leading-relaxed">{alert.desc}</div>
                                <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                                  <i data-lucide="ship" className="w-3 h-3"></i> {alert.vessel}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Year-End Projection Summary */}
                    <div className="bg-linear-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white shadow-lg">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                          <i data-lucide="calculator" className="w-5 h-5 text-indigo-300"></i>
                        </div>
                        <div>
                          <h2 className="text-base font-bold">Year-End Projection Summary</h2>
                          <p className="text-xs text-slate-400">Based on 9 months of operational data</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Annual Budget</div>
                          <div className="text-xl font-black text-white">{fmt(totalBudget)}</div>
                        </div>
                        <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Projected Spend</div>
                          <div className={`text-xl font-black ${totalFac > totalBudget ? 'text-red-400' : 'text-emerald-400'}`}>{fmt(totalFac)}</div>
                        </div>
                        <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Projected Variance</div>
                          <div className={`text-xl font-black ${totalFac > totalBudget ? 'text-red-400' : 'text-emerald-400'}`}>
                            {totalFac > totalBudget ? '+' : ''}{fmt(Math.abs(totalFac - totalBudget))}
                          </div>
                        </div>
                        <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Forecast Accuracy</div>
                          <div className="text-xl font-black text-amber-400">{totalBudget > 0 ? safeNumber((1 - Math.abs(totalFac - totalBudget) / totalBudget) * 100).toFixed(1) : '0.0'}%</div>
                        </div>
                      </div>
                    </div>

                    
                  </div>
                )}

              </>
            )}

            {/* Spacer for floating nav */}
            <div className="h-48 md:h-64 shrink-0 w-full pointer-events-none"></div>
          </div>
        </main>
      </div>

      

      {/* ─── Vessel Detail Modal ─── */}
      {vesselModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={() => setVesselModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${healthDot(vesselModal.healthLabel).replace('bg-', 'bg-opacity-20 bg-')}`}>
                  <div className={`w-3 h-3 rounded-full ${healthDot(vesselModal.healthLabel)}`}></div>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 leading-tight">{vesselModal.name}</h2>
                  <p className="text-xs text-slate-500">{vesselModal.type} • Health Score: {Math.round(vesselModal.healthScore)}/100</p>
                </div>
              </div>
              <button onClick={() => setVesselModal(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">
                <i data-lucide="x" className="w-5 h-5"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5">Allocated</div>
                  <div className="text-sm font-bold text-slate-900">{fmtFull(vesselModal.allocated)}</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5">Utilized</div>
                  <div className="text-sm font-bold text-slate-900">{fmtFull(vesselModal.utilized)}</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5">Remaining</div>
                  <div className="text-sm font-bold text-emerald-600">{fmtFull(vesselModal.remaining)}</div>
                </div>
              </div>

              {/* Variance & FAC */}
              <div className="grid grid-cols-2 gap-3">
                <div className={`rounded-xl p-3 border ${vesselModal.variance > 0 ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5 text-slate-500">Variance</div>
                  <div className={`text-lg font-black ${vesselModal.variance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {vesselModal.variance > 0 ? '+' : ''}{fmtFull(Math.round(vesselModal.variance))}
                  </div>
                </div>
                <div className={`rounded-xl p-3 border ${vesselModal.fac > vesselModal.allocated ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5 text-slate-500">Forecast at Completion</div>
                  <div className={`text-lg font-black ${vesselModal.fac > vesselModal.allocated ? 'text-red-600' : 'text-slate-900'}`}>
                    {fmtFull(Math.round(vesselModal.fac))}
                  </div>
                </div>
              </div>

              {/* Maritime Metrics */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100">
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5 text-indigo-500">Budget Per Operating Day</div>
                  <div className="text-lg font-black text-indigo-700">${vesselModal.budgetPerDay.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                  <div className="text-[10px] text-indigo-400 mt-0.5">{vesselModal.operatingDays} operating days</div>
                </div>
                <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100">
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5 text-indigo-500">Cost Per Nautical Mile</div>
                  <div className="text-lg font-black text-indigo-700">${safeNumber(vesselModal.costPerNm).toFixed(2)}</div>
                  <div className="text-[10px] text-indigo-400 mt-0.5">{safeNumber(vesselModal.nauticalMiles).toLocaleString()} NM sailed</div>
                </div>
              </div>

              {/* Monthly Spend Trend */}
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Monthly Spend Trend</div>
                <div className="flex items-end gap-1 h-24">
                  {vesselModal.monthlySpend.map((val, i) => {
                    const maxSpend = Math.max(...vesselModal.monthlySpend);
                    const h = maxSpend > 0 ? (val / maxSpend) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div
                          className={`w-full rounded-t transition-all ${val > 0 ? (i <= currentMonthIndex ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-slate-200') : 'bg-slate-100'}`}
                          style={{ height: `${Math.max(h, 2)}%` }}
                          title={val > 0 ? `${MONTH_LABELS[i]}: ${fmtFull(val)}` : MONTH_LABELS[i]}
                        ></div>
                        <span className="text-[8px] text-slate-400">{MONTH_LABELS[i].substring(0, 1)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Utilization bar */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Budget Utilization</span>
                  <span className={`text-sm font-bold ${vesselModal.utilizationPct > 80 ? 'text-red-600' : 'text-slate-700'}`}>{pct(vesselModal.utilizationPct)}</span>
                </div>
                <MiniProgress
                  value={vesselModal.utilizationPct}
                  color={vesselModal.utilizationPct > 90 ? 'bg-red-500' : vesselModal.utilizationPct > 75 ? 'bg-amber-500' : 'bg-emerald-500'}
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
              <button onClick={() => setVesselModal(null)} className="w-full py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors shadow-sm">
                Close Details
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

